import { useQuery } from '@tanstack/react-query';
import { QueryFlatPositionsQuery, QueryFlatPositionsQueryVariables } from '__generated__/graphql';
import { POSITION_QUERY } from 'apollo/queries/position-queries';
import { useActiveMember } from 'hooks';
import useGraphQLPaging from 'hooks/graphql/useGraphQLPaging/useGraphQLPaging';
import useReactQueryBaseKey from 'hooks/react-query/useReactQueryBaseKey/useReactQueryBaseKey';
import { castArray, sortBy } from 'lodash';
import { useMemo } from 'react';
import { NonNullArrayField } from 'types/util/NonNullArrayField';
import { TPermissions, canManagePermissionForPosition } from 'utils/positionUtils';

export type PositionPickerItem = NonNullArrayField<QueryFlatPositionsQuery['positions']>;

export function usePositionPickerData(permissions?: TPermissions | TPermissions[]) {
  const activeMember = useActiveMember();
  const baseKey = useReactQueryBaseKey();
  const pageAll = useGraphQLPaging();

  const { data: rawPositions, isLoading } = useQuery({
    queryKey: [...baseKey, 'position-picker-all'],
    queryFn: () =>
      pageAll<QueryFlatPositionsQuery, QueryFlatPositionsQueryVariables, PositionPickerItem>(
        { document: POSITION_QUERY, variables: { filter: { deletedOn: { isNull: true } } } },
        'positions',
        200
      ),
  });

  const positions = useMemo(() => {
    if (!rawPositions) {
      return [];
    }

    let result = rawPositions;

    if (permissions && activeMember.position) {
      const permissionArray = castArray(permissions);
      result = result.filter((p) =>
        permissionArray.every((perm) => canManagePermissionForPosition(perm, activeMember.position!, p))
      );
    }

    return sortBy(result, 'title');
  }, [rawPositions, permissions, activeMember.position]);

  return { positions, isLoading };
}
